import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ListItemPrice from 'LuxKit/List/ListItemPrice'
import { OfferPageActions, takeOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useCallback, useId } from 'react'
import * as React from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import { getMultiRoomDurationOptions } from 'selectors/offerPage/multipleRoomBookingSelectors'
import RadioButton from 'LuxKit/Radio/RadioButton'
import type { OfferDurationOption } from '../DurationTabs/OfferDurationTabs'
import noop from 'lib/function/noop'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import Label from 'LuxKit/Label'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import Pill from 'LuxKit/Pill'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { filterCharterUnbundledDurationOptions, getDurationOptions } from 'selectors/offerPage/offerPageSelectors'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'

function formatSaleUnitLabel(intl: IntlShape | undefined, count: number, saleUnit: string): string | undefined {
  switch (saleUnit) {
    case 'room':
      return intl?.formatMessage({
        id: 'y14xysFFuoGARJvRdS0m',
        defaultMessage: '{count, plural, one {# room} other {# rooms}} from',
        description: 'Pricing label with room count for duration option on the form',
      }, { count })
    case 'suite':
      return intl?.formatMessage({
        id: 'pagalIU5ZzKpQJwxMQ_4',
        defaultMessage: '{count, plural, one {# suite} other {# suites}} from',
        description: 'Pluralized suite count followed by pricing prefix in the duration filter option',
      }, { count })
    case 'villa':
      return intl?.formatMessage({
        id: '2wL-p5JIu5r3qlEh4xG7',
        defaultMessage: '{count, plural, one {# villa} other {# villas}} from',
        description: 'Pluralized villa count followed by pricing prefix in the duration filter option',
      }, { count })
    case 'cabin':
      return intl?.formatMessage({
        id: 'e9xOV7AbD8LUKnOw7vKu',
        defaultMessage: '{count, plural, one {# cabin} other {# cabins}} from',
        description: 'Pluralized cabin count followed by pricing prefix in the duration filter option',
      }, { count })
    default:
      return intl?.formatMessage({
        id: 'AflP9VcLsevQX6FyTzsr',
        defaultMessage: '{count} {saleUnit} from',
        description: 'Pricing label with generic sale unit count for duration option',
      }, { count, saleUnit })
  }
}

export function getOptionLabel(option: OfferDurationOption, roomCount: number = 1, saleUnit: string = 'room', intl?: IntlShape) {
  if (option.soldOut) {
    return intl?.formatMessage({
      id: 'F5ByZ57VadSfU9ZTBFoP',
      defaultMessage: 'Sold out',
      description: 'Label shown when a duration option is sold out on offer page',
    }) ?? 'Sold out'
  } else if (option.lowestPrice === undefined) {
    return undefined
  } else {
    if (roomCount > 1) {
      return formatSaleUnitLabel(intl, roomCount, saleUnit) ?? `${roomCount} ${saleUnit} from`
    }
    return intl?.formatMessage({
      id: 'WXYJI-HcfG0ki3QR-ekH',
      defaultMessage: 'From',
      description: 'Pricing label prefix before duration option price on the offer detail page',
    }) ?? 'From'
  }
}

export function getSubtitleLabel(option: OfferDurationOption, intl?: IntlShape) {
  if (isLPCTacticalEnabled() && option.hasTactical) {
    return <LabelGroup>
      <Label
        startIcon={<SolidFireIcon colour="urgency" size="XS" />}
        variant="critical"
        kind="plain"
      >
        {intl?.formatMessage({
          id: 'gR2iWPJUZECvhkY37rs_',
          defaultMessage: 'Limited Time Special',
          description: 'Label for a tactical limited-time promotion on a duration option',
        })}
      </Label>
      {(option.discountPercent ?? 0) > 0 && <Pill kind="primary" variant="success">{intl?.formatMessage(
        {
          id: 'kbAgQU8wDQmuFI9EMDJa',
          defaultMessage: '-{discount}%',
          description: 'Discount percentage shown on duration option on the offer detail page',
        },
        { discount: option.discountPercent },
      )}
      </Pill>}
    </LabelGroup>
  }

  return undefined
}

interface Props {
  durationOptions: Array<OfferDurationOption>
  selectedDuration: number
  onClose?: () => void
  bundledWithFlightsOnly: boolean
  currentOfferId?: string
  bundleOfferId?: string
  onSelect?: (duration: number) => void
  rooms?: Array<App.RoomOccupants>
  saleUnit: string
  displayMemberPrice?: boolean
}

function OfferDurationOptions(props: Props) {
  const {
    durationOptions,
    onClose,
    selectedDuration,
    bundledWithFlightsOnly,
    currentOfferId,
    bundleOfferId,
    rooms,
    saleUnit,
    onSelect = noop,
    displayMemberPrice,
  } = props
  const intl = useIntl()
  const dispatch = useOfferPageDispatch()

  const name = useId()

  const onDurationClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const duration = parseInt(e.currentTarget.dataset.duration!, 10)
    dispatch({ type: OfferPageActions.setDuration, duration, offerId: currentOfferId })
    onClose?.()
    onSelect(duration)
  }, [dispatch, currentOfferId, onClose, onSelect])
  return <>
    {durationOptions.map(option => {
      const price = displayMemberPrice && option.lowestMemberPrice ? option.lowestMemberPrice : option.lowestPrice
      const roundedLowestPrice = price ? Math.ceil(price) : price
      return <ClickableListItem
        key={option.duration}
        title={intl.formatMessage({
          id: 'ZN9-HTVegsVlxrMI9XS4',
          defaultMessage: '{count, plural, one {# night} other {# nights}}',
          description: 'Duration option title showing night count on the offer detail page',
        }, { count: option.duration })}
        subtitle={getSubtitleLabel(option, intl)}
        data-duration={option.duration}
        onClick={onDurationClick}
        startIcon={<RadioButton name={name} value={option.duration} checked={option.duration === selectedDuration} />}
        disabled={option.soldOut}
      >
        <ListItemPrice
          price={(option.soldOut || bundledWithFlightsOnly || bundleOfferId) ? undefined : roundedLowestPrice}
          label={bundledWithFlightsOnly || bundleOfferId ? undefined : getOptionLabel(option, rooms ? rooms.length : 1, saleUnit, intl)}
          priceColour={displayMemberPrice ? 'lux-plus' : undefined}
        />
      </ClickableListItem> },
    )}
  </>
}

export default withOfferPageAndReduxState((state, appState) => {
  const currentOfferId = state.paginator?.currentOfferId
  const currentState = takeOfferState(state, currentOfferId)
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)

  const baseDurationOptions = isMultiRoom ? getMultiRoomDurationOptions(currentState, appState) : getDurationOptions(state, appState)
  const durationOptions = (currentState.charterBundleRoute && !currentState.charterBundleEnabled)
    ? filterCharterUnbundledDurationOptions(baseDurationOptions)
    : baseDurationOptions

  return {
    selectedDuration: currentState.duration,
    bundleOfferId: currentState.bundleOffer?.id,
    currentOfferId,
    displayMemberPrice: state.displayMemberPrice,
    durationOptions,
    bundledWithFlightsOnly: currentState.offer?.bundledWithFlightsOnly,
    rooms: isMultiRoom ? state.rooms : undefined,
    saleUnit: state.offer?.saleUnit ?? 'room',
  }
}, OfferDurationOptions)
